const request = require('concora-request');
const _ = require('lodash');
const EventEmitter = require('events').EventEmitter;
const io = require('socket.io-client');
const LLCollector = require('./ll-collector');

class LaunchLightlyClient extends EventEmitter {
  constructor(opts) {
    super();
    this.options = _.defaults({}, opts, {
      refreshAttributes: 60000,
      initialConfig: {},
      useLaunchLightly: true,
      overrides: {},
      metadata: {},
      collect: false,
    });

    // use NODE_ENV as a sensible default until we're told otherwise
    this.environment = process.env.NODE_ENV;

    let endpoint = this.options.endpoint;
    if (!endpoint) {
      if (_.has(this.options.overrides, 'launchlightly.endpoint')) {
        endpoint = _.get(this.options.overrides, 'launchlightly.endpoint');
      }
      if (!endpoint) {
        endpoint = 'https://launchlightly.bimsource.io';
      }
    }

    this.options.endpoint = endpoint;
    if (!this.options.socketEndpoint) {
      this.options.socketEndpoint = endpoint;
    }

    this.request = request.create({ baseURL: this.options.endpoint });

    if (this.options.useLaunchLightly) {
      if (!this.options.token) {
        throw new Error('"token" not specified');
      }
    }

    this.overrides = this.options.overrides;
    this.initialConfig = this.options.initialConfig;
    this.allFeatures = {};

    // user didn't give us a log, so we need to make one.
    if (!opts.log) {
      // create a simple logger until we have more info to create a real one
      this.log = console;

      this.collector = new LLCollector({
        log: this.log,
        collect: this.options.collect,
        request: this.request,
        token: this.options.token,
      });

      let initialLevel = this.getString('logLevel', {}, 'info');
      let service = this.getString('server.name', {}, 'll-client');

      // we have more info, create a better logger.
      //this.log = this.log.child({ service, level: initialLevel });
      this.collector.setOpts({ log: this.log });
      this.log.debug('initial level was', initialLevel);
    } else {
      // we have a logger already, no need to make a new one
      this.log = opts.log;
      this.collector = new LLCollector({
        log: this.log,
        collect: this.options.collect,
        request: this.request,
        token: this.options.token,
      });
    }

    this.collector.setOpts({
      metadata: _.defaults(this.options.metadata, {
        client: this.getString('server.name', {}, 'll-client'),
      }),
      analyticsInterval: this.getInteger('ll.collector.interval', {}, 60000),
      analyticsJitter: this.getInteger('ll.collector.jitter', {}, 15000),
    });
    this.ready = false;
  }

  async promise() {
    this.connect();
    return new Promise((resolve, reject) => {
      if (this.ready) {
        resolve(this);
      } else {
        this.once('ready', () => {
          resolve(this);
        });
      }
    });
  }

  isNumeric(num) {
    return !isNaN(num);
  }
  isFloat(num) {
    return this.isNumeric(num) && num.toString().indexOf('.') !== -1;
  }
  isInt(num) {
    return this.isNumeric(num) && !this.isFloat();
  }
  isBoolean(val) {
    return val.toLowerCase() === 'true' || val.toLowerCase() === 'false';
  }
  parseBoolean(val) {
    return val.toLowerCase() === 'true';
  }
  parseNumber(num) {
    return this.isFloat(num) ? parseFloat(num) : parseInt(num, 10);
  }

  guessDatatype(value) {
    let datatype = typeof value;
    if (Array.isArray(value)) {
      datatype = 'array';
    }

    if (datatype === 'string') {
      // further guess because string is the default
      if (value === '') {
        // assume string
      } else if (this.isNumeric(value)) {
        datatype = 'number';
        value = this.parseNumber(value);
      } else if (this.isBoolean(value)) {
        datatype = 'boolean';
        value = this.parseBoolean(value);
      }
    }

    return { datatype, value };
  }

  async getAllFeatures(token) {
    this.allFeatures = await this.request({
      url: `/client/features`,
      method: 'GET',
      headers: {
        'x-api-key': token,
      },
    });
  }

  async getInitialFeatureList() {
    if (!this.options.useLaunchLightly) {
      //this.log.warn('Asking for feature list but not using LL, returning initial config instead');
      return this.overrides;
    }
    try {
      this.log.info('Connected to launchlightly');
      this.log.debug('Requesting initial list of flags');
      let featuresResponse = await this.request({
        url: `/client/features`,
        method: 'GET',
        headers: {
          'x-api-key': this.options.token,
        },
      });

      this.log.debug('raw features response', featuresResponse);

      if (featuresResponse.environment && this.environment !== 'test') {
        // override NODE_ENV with what LL told us we're using as long as we aren't in tests.
        // tests are kind of a special case we don't want to override because we may eventually
        // want to use LL in tests.
        this.environment = featuresResponse.environment;
      }

      this.allFeatures = {};
      // we'll need to loop through features to get all of them eventually
      featuresResponse.features.forEach((x) => {
        this.allFeatures[x.name] = x;
      });

      // now that we have the features list, remove initial configuration
      this.initialConfig = {};

      this.ready = true;
      this.emit('ready');
      let service = this.getString('server.name', {}, 'll-client');
      //this.log = this.log.child({ service });
      this.log.info('Retrieved configuration from launchlightly');
    } catch (e) {
      this.log.error('Error getting full feature list', e);
      return new Promise((resolve) => {
        setTimeout(() => resolve(this.getInitialFeatureList()), 1000);
      });
    }
  }

  toUpperCase(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.collector.setOpts({ socket: null });
    this.collector.stop();
  }

  connect(callback) {
    this.callback = callback;
    // noop if not using LL
    if (!this.options.useLaunchLightly) {
      setImmediate(() => {
        this.ready = true;
        this.emit('ready');
      });
      return;
    }
    if (this.socket) {
      this.log.debug('already have a socket, ignoring.');
      return;
    }

    this.log.info('About to connect to launchlightly socket ', this.options.socketEndpoint);
    let socket = io(this.options.socketEndpoint, {
      query: { token: this.options.token },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', async () => {
      this.log.debug('socket connected, getting feature list');
      await this.getInitialFeatureList();
      this.log.info('launchlightly fully configured');
      this.callback && (await this.callback());
    });

    socket.on('error', (err) => {
      this.log.error('Error over launchlightly socket connection', err);
    });

    socket.on('connect_error', (err) => {
      this.log.error('socket connect error', err);
    });

    socket.on('connect_timeout', (timeout) => {
      this.log.error('socket connect timeout', timeout);
    });

    socket.on('connect_timeout', (timeout) => {
      this.log.error('socket connect timeout', timeout);
    });

    socket.on('reconnect_attempt', (num) => {
      this.log.debug('socket reconnect_attempt', num);
    });

    socket.on('reconnect', () => {
      this.log.debug('socket reconnected');
    });

    socket.on('reconnecting', () => {
      this.log.debug('socket reconnecting');
    });

    socket.on('reconnect_error', (err) => {
      this.log.error('socket reconnect error', err);
    });

    socket.on('unauthorized', (err) => {
      this.log.error('got unauthorized error', err);
    });

    socket.on('authorized', async () => {
      this.log.info('got authorized notification');
    });

    socket.on('createFeatureValue', (data) => {
      this.log.debug('createFeatureValue happened', data);
      data.id = data.feature_id;
      this.allFeatures[data.name] = data;
      this.log.debug('new feature list:', { allFeatures: this.allFeatures });
      this.emit('createFeatureValue', data);
    });
    socket.on('updateFeatureValue', (data) => {
      this.log.debug('updateFeatureValue happened, received payload', data);
      let name = Object.values(this.allFeatures).find((x) => x.id === data.feature_id).name;
      this.allFeatures[name].value = data.value;

      this.log.debug('new feature list:', { allFeatures: this.allFeatures });
      this.emit('updateFeatureValue', data);
      this.emit(`update:${data.name}`, data);
    });
    socket.on('disconnect', () => {
      this.log.info('bye server');
      this.ready = false;
    });

    this.socket = socket;
    this.collector.setOpts({ socket: this.socket });
  }

  getString(name, context, defaultValue = '') {
    let feature = this.getFeature(name, context, defaultValue);
    this.collector.push('getString', name, context, defaultValue, feature);
    this.validateType(feature, 'string');
    return feature.value;
  }

  getBoolean(name, context, defaultValue = false) {
    let feature = this.getFeature(name, context, defaultValue);
    this.collector.push('getBoolean', name, context, defaultValue, feature);
    this.validateType(feature, 'boolean');
    return feature.value;
  }

  getInteger(name, context, defaultValue = 0) {
    return parseInt(this.getNumber(name, context, defaultValue, true));
  }

  getNumber(name, context, defaultValue = 0, isInt = false) {
    let feature = this.getFeature(name, context, defaultValue);
    let method = isInt ? 'getInteger' : 'getNumber';
    this.collector.push(method, name, context, defaultValue, feature);
    this.validateType(feature, 'number');
    return feature.value;
  }

  getObject(name, context, defaultValue = {}) {
    let feature = this.getFeature(name, context, defaultValue);
    this.collector.push('getObject', name, context, defaultValue, feature);
    this.validateType(feature, 'object');
    return feature.value;
  }

  getArray(name, context, defaultValue = []) {
    let feature = this.getFeature(name, context, defaultValue);
    this.collector.push('getArray', name, context, defaultValue, feature);
    this.validateType(feature, 'array');
    return feature.value;
  }

  get(name, context, defaultValue = '') {
    // untyped get.  Discouraged; use a typed get instead for better safety.
    // this is provided for compatibility with 'config' library
    this.log.debug('accessing feature via .get(), use type-safe version instead', {
      feature: name,
    });
    let feature = this.getFeature(name, context, defaultValue);
    this.collector.push('get', name, context, defaultValue, feature);
    return feature.value;
  }

  has(name, context) {
    let feature = this.getFeature(name, context, undefined);
    this.collector.push('has', name, context, undefined, feature);
    return feature.value !== undefined;
  }

  getFeature(name, context, defaultValue = undefined) {
    // this gets the raw feature object
    // clients using this API should favor getObject, getBoolean, etc.
    // context is like { user }
    // ignored for now.

    // if there is an env property, use that as an override
    // this allows ie devs to configure their DB settings to override those coming from LL
    // shouldn't be used in production.

    // check if defined as a process.env var
    if (process.env[name] !== undefined) {
      this.log.debug('Using env var override for name', name);
      let { datatype, value } = this.guessDatatype(process.env[name]);

      return {
        id: null,
        name,
        value,
        datatype,
        source: 'env',
      };
    }

    // check if defined as a local override
    if (_.has(this.overrides, name)) {
      this.log.debug('Using local config var override for name', name);
      let { datatype, value } = this.guessDatatype(_.get(this.overrides, name));
      this.log.debug('assuming datatype', datatype, name, value);
      return {
        id: null,
        name,
        value,
        datatype,
        source: 'override',
      };
    }

    // check if in the initial set of features
    // note this basically the same logic as a local override,
    // but will be overwritten when we get the feature list from LL
    if (_.has(this.initialConfig, name)) {
      this.log.debug('Using initial config var for name', name);
      let { datatype, value } = this.guessDatatype(_.get(this.initialConfig, name));
      this.log.debug('assuming datatype', datatype, name, value);
      return {
        id: null,
        name,
        value,
        datatype,
        source: 'initial',
      };
    }

    // check if in set of features from LL
    if (this.allFeatures.hasOwnProperty(name)) {
      let featureValue = _.get(this.allFeatures, name);
      return _.assign({}, featureValue, { source: 'flag' });
    }

    // we don't know, we give up.  give back the default value they gave us.
    let { datatype, value } = this.guessDatatype(defaultValue);
    return {
      id: null,
      name,
      value,
      datatype,
      source: 'default',
    };
  }

  validateType(feature, expectedType) {
    if (expectedType !== feature.datatype) {
      throw new Error(`Expected ${expectedType} but received ${feature.datatype}`);
    }
  }
}

module.exports = LaunchLightlyClient;
